/**
 * THE QUANTITY STEP. Tapping a food no longer adds one at once: it asks how
 * many, right where the customer tapped, with the box and the Add button on the
 * SAME ROW so the whole thing is one thumb movement.
 *
 * The box is a real number field (numeric keypad on a phone, opens focused with
 * the value selected so typing replaces it, Enter adds) with minus and plus at
 * 44px beside it. Empty, zero and anything that is not a number are refused in
 * the app's warm voice — never a red browser error.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Minus, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import GroceryIcon from './GroceryIcon';

export const QtyRow = ({ value, onChange, onAdd, max = 99, packages = false, busy = false,
    autoFocus = true, testId = 'grocery-qty' }) => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const [raw, setRaw] = useState(String(value));

    useEffect(() => { setRaw(String(value)); }, [value]);
    useEffect(() => {
        if (!autoFocus) return;
        const id = setTimeout(() => { ref.current?.focus(); ref.current?.select(); }, 120);
        return () => clearTimeout(id);
    }, [autoFocus]);

    const clean = () => {
        const n = Number.parseInt((raw || '').replace(/[^\d]/g, ''), 10);
        if (!Number.isFinite(n) || n < 1) return null;
        return Math.min(n, max);
    };

    const commit = () => {
        const n = clean();
        if (n === null) {
            toast.error(t('grocery.qtyInvalid', { max }));
            setRaw(String(value));
            return null;
        }
        onChange(n);
        setRaw(String(n));
        return n;
    };

    const addNow = () => {
        const n = commit();
        if (n !== null) onAdd(n);
    };

    return (
        <div className="flex items-center gap-2" data-testid={testId}>
            <label className="shrink-0 text-sm font-bold text-foreground" htmlFor={`${testId}-input`}>
                {t('grocery.qty')}
            </label>
            <button onClick={() => onChange(Math.max(1, value - 1))} disabled={value <= 1 || busy}
                data-testid={`${testId}-minus`} aria-label={t('grocery.fewer')}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-input text-foreground disabled:opacity-40">
                <Minus className="h-5 w-5" />
            </button>
            <input ref={ref} id={`${testId}-input`} data-testid={`${testId}-input`}
                type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="off"
                value={raw} onChange={(e) => setRaw(e.target.value.replace(/[^\d]/g, ''))}
                onBlur={commit}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNow(); } }}
                aria-label={t('grocery.qty')}
                className="h-11 w-16 shrink-0 rounded-xl border-2 border-input bg-background text-center text-xl font-black tabular-nums text-foreground" />
            <button onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max || busy}
                data-testid={`${testId}-plus`} aria-label={t('grocery.more')}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-input text-foreground disabled:opacity-40">
                <Plus className="h-5 w-5" />
            </button>
            <button onClick={addNow} disabled={busy} data-testid={`${testId}-add`}
                data-launcher-avoid="lift"
                className="flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary px-3 text-sm font-bold text-primary-foreground disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 shrink-0" />}
                <span className="truncate">{t('grocery.addBtn')}</span>
            </button>
            {packages ? (
                <span className="sr-only" data-testid={`${testId}-packages`}>{t('grocery.packages')}</span>
            ) : null}
        </div>
    );
};

/** The same row, holding its own number — for changing a quantity on the list. */
export const QtyEditRow = ({ value, onAdd, max, packages, testId }) => {
    const [q, setQ] = useState(Math.max(1, Number(value) || 1));
    return <QtyRow value={q} onChange={setQ} onAdd={onAdd} max={max} packages={packages}
        testId={testId} />;
};

/** The small panel itself: the food, its remembered choice, and the row. */
export const QtyPanel = ({ food, max, packages, onAdd, onCancel, onEditOptions, busy,
    options: optionsCard = null, testId = 'grocery-qty-panel' }) => {
    const { t } = useTranslation();
    const [qty, setQty] = useState(Math.max(1, Number(food.last_qty) || 1));
    const options = (food.options || []).filter(Boolean);
    return (
        <div data-testid={testId}
            className="space-y-3 rounded-2xl border-2 border-primary/40 bg-white p-3 shadow-lg dark:bg-slate-900">
            <div className="flex items-start gap-3">
                {food.icon ? <GroceryIcon icon={food.icon} badge={food.badge} size={40} alt="" /> : null}
                <div className="min-w-0 flex-1">
                    {/* The whole name, wrapping — never cut with an ellipsis. */}
                    <p className="break-words text-base font-bold leading-snug text-foreground"
                        data-testid={`${testId}-name`}>{food.name}</p>
                    {options.length ? (
                        <button onClick={onEditOptions} disabled={!onEditOptions}
                            data-testid={`${testId}-options`}
                            className="text-left text-xs font-semibold text-foreground underline decoration-dotted underline-offset-2 disabled:no-underline">
                            {options.join(' · ')}{onEditOptions ? ` · ${t('grocery.change')}` : ''}
                        </button>
                    ) : null}
                    {packages ? (
                        <p className="text-xs text-muted-foreground">{t('grocery.packages')}</p>
                    ) : null}
                </div>
                <button onClick={onCancel} data-testid={`${testId}-cancel`}
                    aria-label={t('grocery.cancel')}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-muted-foreground">
                    <X className="h-5 w-5" />
                </button>
            </div>
            {/* WHAT KIND, WHICH BRAND — above the number, same card. */}
            {optionsCard}
            <QtyRow value={qty} onChange={setQty} onAdd={onAdd} max={max} packages={packages}
                busy={busy} testId={`${testId}-row`} />
        </div>
    );
};

export default QtyPanel;
